
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface LuxuryChatLoadingStateProps {
  username?: string;
  onRetry: () => void;
  loadingStage?: 'connecting' | 'loading-messages' | 'finalizing';
}

const LoadingMessages = [
  { stage: 'connecting', message: 'Conectando com segurança...' },
  { stage: 'loading-messages', message: 'Carregando conversa...' },
  { stage: 'finalizing', message: 'Quase pronto...' }
];

export const LuxuryChatLoadingState: React.FC<LuxuryChatLoadingStateProps> = ({ 
  username, 
  onRetry,
  loadingStage = 'connecting'
}) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);

  // Simulate loading progress
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 300);

    return () => clearInterval(interval);
  }, []);

  // Stage progression simulation
  useEffect(() => {
    const stageTimer = setTimeout(() => {
      if (currentStage < LoadingMessages.length - 1) {
        setCurrentStage(prev => prev + 1);
      }
    }, 2000);

    return () => clearTimeout(stageTimer);
  }, [currentStage]);

  const currentMessage = LoadingMessages[currentStage];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black lg:pl-64 relative overflow-hidden">
      {/* Gradient overlay for luxury effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-luxury-gold/5 via-transparent to-luxury-gold/5" />
      
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-lg border-b border-luxury-gold/20 py-3 px-4 flex items-center fixed top-0 left-0 right-0 lg:left-64 z-10">
        <Link to="/main/chats" className="mr-3">
          <Button variant="ghost" size="icon" className="h-9 w-9 text-luxury-gold/70 hover:text-luxury-gold hover:bg-luxury-gold/10">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        
        <div className="flex items-center flex-1">
          {/* Avatar placeholder with shimmer */}
          <div className="relative h-9 w-9 rounded-full bg-gradient-to-br from-luxury-gold/20 to-luxury-gold/10 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-luxury-gold/20 to-transparent animate-shimmer" />
          </div>
          
          <div className="ml-3">
            <div className="h-4 w-32 bg-luxury-gold/20 rounded animate-pulse" />
            <div className="h-3 w-20 bg-luxury-gold/10 rounded mt-1 animate-pulse" />
          </div>
        </div>
      </div>
      
      {/* Main loading content */}
      <div className="flex items-center justify-center h-full pt-16">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center max-w-md px-6"
        >
          {/* Luxury loading spinner */}
          <div className="relative mb-8">
            <div className="w-20 h-20 mx-auto relative">
              {/* Outer ring */}
              <div className="absolute inset-0 rounded-full border-2 border-luxury-gold/20" />
              
              {/* Animated progress ring */}
              <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 80 80">
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  fill="none"
                  stroke="url(#gradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${2 * Math.PI * 36 * (1 - progress / 100)}`}
                  className="transition-all duration-500 ease-out"
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#D4AF37" />
                    <stop offset="100%" stopColor="#FFD700" />
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Inner pulsing dot */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-3 h-3 bg-luxury-gold rounded-full animate-pulse" />
              </div>
            </div>
          </div>
          
          {/* Username display */}
          {username && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-6"
            >
              <h2 className="text-xl font-serif text-luxury-gold mb-1">
                {username}
              </h2>
              <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-luxury-gold to-transparent mx-auto" />
            </motion.div>
          )}
          
          {/* Loading stage messages */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStage}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="mb-8"
            >
              <p className="text-white/80 text-sm mb-2">
                {currentMessage.message}
              </p>
              <div className="text-luxury-gold/60 text-xs">
                {Math.round(progress)}% concluído
              </div>
            </motion.div>
          </AnimatePresence>
          
          {/* Stage indicators */}
          <div className="flex justify-center gap-2 mb-8">
            {LoadingMessages.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  index <= currentStage 
                    ? 'bg-luxury-gold' 
                    : 'bg-luxury-gold/20'
                }`}
              />
            ))}
          </div>
          
          {/* Retry button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <Button
              onClick={onRetry}
              variant="outline"
              size="sm"
              className="border-luxury-gold/30 text-luxury-gold hover:bg-luxury-gold/10 hover:border-luxury-gold/50 transition-colors duration-300"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar novamente
            </Button>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Bottom fade effect */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
    </div>
  );
};
